import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { pricingAdminService } from '../../services/admin-pricing.service.js';
import {
  PricingProfile,
  PricingRule,
  PricingContract,
  PricingSimulationResponse,
  PricingSyncResponse,
  PricingScopeType,
  PricingProfileType,
  PricingRateType,
} from '../../types/index.js';
import {
  getAdminJwt,
  getAdminToken,
  setAdminJwt,
  setAdminToken,
  getPricingCustomerId,
  setPricingCustomerId,
} from '../../utils/pricingContext.js';
import { decodeJwtPayload } from '../../utils/jwt.js';
import './admin-shared.css';
import './PricingAdminPage.css';

const PROFILE_TYPES: PricingProfileType[] = ['PUBLIC', 'MSP', 'CONTRACT'];
const RATE_TYPES: PricingRateType[] = ['GP', 'MARKUP'];
const RULE_SCOPES: PricingScopeType[] = ['GLOBAL', 'BRAND', 'CATEGORY', 'TAG', 'SKU'];

const parseBoolean = (value: FormDataEntryValue | null): boolean | undefined => {
  if (value === null) return undefined;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const parseNumber = (value: FormDataEntryValue | null): number | undefined => {
  if (value === null || value === '') return undefined;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseOptionalInt = (value: string): number | undefined => {
  if (!value.trim()) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const PricingAdminPage: React.FC = () => {
  const [adminTokenValue, setAdminTokenValue] = useState(getAdminToken() ?? '');
  const [activeToken, setActiveToken] = useState(getAdminToken() ?? '');
  const [adminJwtValue, setAdminJwtValue] = useState(getAdminJwt() ?? '');
  const [activeJwt, setActiveJwt] = useState(getAdminJwt() ?? '');
  const [customerIdValue, setCustomerIdValue] = useState(getPricingCustomerId() ?? '');
  const [profiles, setProfiles] = useState<PricingProfile[]>([]);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [contracts, setContracts] = useState<PricingContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [simulationResult, setSimulationResult] = useState<PricingSimulationResponse | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<PricingSyncResponse | null>(null);
  const storeToken = useGlobalStore((s: GlobalState) => s.token);

  const [syncForm, setSyncForm] = useState({
    source: 'halo',
    clientHaloId: '',
    contractHaloId: '',
    dryRun: true,
  });

  const roleSummary = useMemo(() => {
    const tokenSource = storeToken?.trim() || activeJwt.trim();
    if (!tokenSource) return null;
    const payload = decodeJwtPayload(tokenSource);
    if (!payload) return 'JWT present (roles unavailable)';
    const roles = payload.roles?.length ? payload.roles.join(', ') : 'No roles found';
    return storeToken ? `IDP roles: ${roles}` : `JWT roles: ${roles}`;
  }, [activeJwt, storeToken]);

  const [newProfile, setNewProfile] = useState({
    name: 'Contract Default',
    profileType: 'CONTRACT' as PricingProfileType,
    rateType: 'GP' as PricingRateType,
    gpRateBasisPoints: 1500,
    priority: 100,
    isActive: true,
  });

  const [newRule, setNewRule] = useState({
    profileId: '',
    scopeType: 'SKU' as PricingScopeType,
    scopeValue: '',
    gpRateBasisPoints: 1500,
    priority: 100,
    isActive: true,
    validFrom: '',
    validTo: '',
  });

  const [newContract, setNewContract] = useState({
    customerExternalId: '',
    profileId: '',
    validFrom: '',
    validTo: '',
    isActive: true,
    notes: '',
  });

  const [simulation, setSimulation] = useState({
    brandId: '',
    productId: '',
    customerExternalId: '',
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [profilesResult, rulesResult, contractsResult] = await Promise.all([
        pricingAdminService.listProfiles(),
        pricingAdminService.listRules(),
        pricingAdminService.listContracts(),
      ]);
      setProfiles(profilesResult);
      setRules(rulesResult);
      setContracts(contractsResult);
      if (!newRule.profileId && profilesResult.length > 0) {
        setNewRule((prev) => ({ ...prev, profileId: profilesResult[0].id }));
      }
      if (!newContract.profileId && profilesResult.length > 0) {
        setNewContract((prev) => ({ ...prev, profileId: profilesResult[0].id }));
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  }, [newContract.profileId, newRule.profileId]);

  useEffect(() => {
    if (storeToken?.trim() || activeJwt.trim() || activeToken.trim()) {
      loadAll();
    }
  }, [activeJwt, activeToken, loadAll, storeToken]);

  const handleTokenSave = () => {
    setAdminToken(adminTokenValue);
    setActiveToken(adminTokenValue);
    loadAll();
  };

  const handleJwtSave = () => {
    setAdminJwt(adminJwtValue);
    setActiveJwt(adminJwtValue);
    loadAll();
  };

  const handleCustomerIdSave = () => {
    setPricingCustomerId(customerIdValue);
  };

  const handleCreateProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    try {
      await pricingAdminService.createProfile({
        name: newProfile.name,
        profileType: newProfile.profileType,
        rateType: newProfile.rateType,
        gpRateBasisPoints: newProfile.gpRateBasisPoints,
        priority: newProfile.priority,
        isActive: newProfile.isActive,
      });
      await loadAll();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create profile');
    }
  };

  const handleUpdateProfile = async (event: React.FormEvent<HTMLFormElement>, profileId: string) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    await pricingAdminService.updateProfile(profileId, {
      name: String(formData.get('name') ?? ''),
      gpRateBasisPoints: parseNumber(formData.get('gpRateBasisPoints')),
      rateType: formData.get('rateType') as PricingRateType,
      priority: parseNumber(formData.get('priority')),
      isActive: parseBoolean(formData.get('isActive')),
    });
    await loadAll();
  };

  const handleCreateRule = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    try {
      await pricingAdminService.createRule({
        profileId: newRule.profileId,
        scopeType: newRule.scopeType,
        scopeValue: newRule.scopeValue || undefined,
        gpRateBasisPoints: newRule.gpRateBasisPoints,
        priority: newRule.priority,
        isActive: newRule.isActive,
        validFrom: newRule.validFrom || undefined,
        validTo: newRule.validTo || undefined,
      });
      await loadAll();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create rule');
    }
  };

  const handleUpdateRule = async (event: React.FormEvent<HTMLFormElement>, ruleId: string) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    await pricingAdminService.updateRule(ruleId, {
      scopeType: formData.get('scopeType') as PricingScopeType,
      scopeValue: String(formData.get('scopeValue') ?? '') || undefined,
      gpRateBasisPoints: parseNumber(formData.get('gpRateBasisPoints')),
      priority: parseNumber(formData.get('priority')),
      isActive: parseBoolean(formData.get('isActive')),
      validFrom: String(formData.get('validFrom') ?? '') || undefined,
      validTo: String(formData.get('validTo') ?? '') || undefined,
    });
    await loadAll();
  };

  const handleDeleteRule = async (ruleId: string) => {
    await pricingAdminService.deleteRule(ruleId);
    await loadAll();
  };

  const handleCreateContract = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    try {
      await pricingAdminService.createContract({
        customerExternalId: newContract.customerExternalId,
        profileId: newContract.profileId,
        validFrom: newContract.validFrom || undefined,
        validTo: newContract.validTo || undefined,
        isActive: newContract.isActive,
        notes: newContract.notes || undefined,
      });
      await loadAll();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create contract');
    }
  };

  const handleUpdateContract = async (event: React.FormEvent<HTMLFormElement>, contractId: string) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    await pricingAdminService.updateContract(contractId, {
      customerExternalId: String(formData.get('customerExternalId') ?? ''),
      profileId: String(formData.get('profileId') ?? ''),
      validFrom: String(formData.get('validFrom') ?? '') || undefined,
      validTo: String(formData.get('validTo') ?? '') || undefined,
      isActive: parseBoolean(formData.get('isActive')),
      notes: String(formData.get('notes') ?? '') || undefined,
    });
    await loadAll();
  };

  const handleDeleteContract = async (contractId: string) => {
    await pricingAdminService.deleteContract(contractId);
    await loadAll();
  };

  const handleSimulate = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSimulationResult(null);
    try {
      const result = await pricingAdminService.simulate({
        brandId: Number.parseInt(simulation.brandId, 10),
        productId: simulation.productId,
        customerExternalId: simulation.customerExternalId || undefined,
      });
      setSimulationResult(result);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Simulation failed');
    }
  };

  const handleSyncContracts = async (event: React.FormEvent) => {
    event.preventDefault();
    setSyncError(null);
    setSyncResult(null);
    setSyncStatus('loading');
    try {
      const result = await pricingAdminService.syncContracts({
        source: syncForm.source === 'msp-ai' ? 'msp-ai' : 'halo',
        clientHaloId: parseOptionalInt(syncForm.clientHaloId),
        contractHaloId: parseOptionalInt(syncForm.contractHaloId),
        dryRun: syncForm.dryRun,
      });
      setSyncResult(result);
      setSyncStatus('done');
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Sync failed');
      setSyncStatus('idle');
    }
  };

  return (
    <div className="pricing-admin-page">
      <section className="page-hero pricing-admin-hero">
        <div className="container">
          <span className="badge badge-lime">Admin</span>
          <h1>Pricing Control Center</h1>
          <p>Manage contract GP rules, map customers, and verify pricing outcomes.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="admin-card">
            <h2>Access</h2>
            <p>Use a JWT access token for role-based access, or the legacy admin token.</p>
            <div className="admin-input-row">
              <input
                type="password"
                value={adminJwtValue}
                onChange={(event) => setAdminJwtValue(event.target.value)}
                placeholder="Admin JWT (preferred)"
              />
              <button type="button" className="btn btn-primary" onClick={handleJwtSave}>
                Save JWT
              </button>
            </div>
            <div className="admin-input-row">
              <input
                type="password"
                value={adminTokenValue}
                onChange={(event) => setAdminTokenValue(event.target.value)}
                placeholder="Legacy admin token (fallback)"
              />
              <button type="button" className="btn btn-outline" onClick={handleTokenSave}>
                Save Token
              </button>
            </div>
            <div className="admin-input-row">
              <input
                type="text"
                value={customerIdValue}
                onChange={(event) => setCustomerIdValue(event.target.value)}
                placeholder="Pricing customer ID (optional)"
              />
              <button type="button" className="btn btn-outline" onClick={handleCustomerIdSave}>
                Save Context
              </button>
            </div>
            {roleSummary && <p className="status-message">{roleSummary}</p>}
            {loading && <p className="status-message">Loading pricing data...</p>}
            {errorMessage && <p className="status-message status-error">{errorMessage}</p>}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="admin-grid">
            <div className="admin-panel">
              <h2>Profiles</h2>
              <form className="admin-form" onSubmit={handleCreateProfile}>
                <input
                  type="text"
                  value={newProfile.name}
                  onChange={(event) => setNewProfile((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Profile name"
                  required
                />
                <select
                  value={newProfile.profileType}
                  onChange={(event) => setNewProfile((prev) => ({ ...prev, profileType: event.target.value as PricingProfileType }))}
                >
                  {PROFILE_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <select
                  value={newProfile.rateType}
                  onChange={(event) => setNewProfile((prev) => ({ ...prev, rateType: event.target.value as PricingRateType }))}
                >
                  {RATE_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={newProfile.gpRateBasisPoints}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setNewProfile((prev) => ({
                      ...prev,
                      gpRateBasisPoints: Number.isFinite(parsed) ? parsed : 0,
                    }));
                  }}
                  placeholder="Rate bps"
                />
                <input
                  type="number"
                  value={newProfile.priority}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setNewProfile((prev) => ({
                      ...prev,
                      priority: Number.isFinite(parsed) ? parsed : 0,
                    }));
                  }}
                  placeholder="Priority"
                />
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={newProfile.isActive}
                    onChange={(event) => setNewProfile((prev) => ({ ...prev, isActive: event.target.checked }))}
                  />
                  Active
                </label>
                <button type="submit" className="btn btn-primary">Create Profile</button>
              </form>

              <div className="admin-list">
                {profiles.map((profile) => (
                  <form
                    key={profile.id}
                    className="admin-list-item"
                    onSubmit={(event) => handleUpdateProfile(event, profile.id)}
                  >
                    <div>
                      <strong>{profile.name}</strong>
                      <span className="muted">{profile.profileType} · {profile.rateType}</span>
                    </div>
                    <div className="admin-fields">
                      <input name="name" defaultValue={profile.name} />
                      <select name="rateType" defaultValue={profile.rateType}>
                        {RATE_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <input name="gpRateBasisPoints" type="number" defaultValue={profile.gpRateBasisPoints} />
                      <input name="priority" type="number" defaultValue={profile.priority} />
                      <select name="isActive" defaultValue={String(profile.isActive)}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <button type="submit" className="btn btn-outline">Save</button>
                  </form>
                ))}
              </div>
            </div>

            <div className="admin-panel">
              <h2>Rules</h2>
              <form className="admin-form" onSubmit={handleCreateRule}>
                <select
                  value={newRule.profileId}
                  onChange={(event) => setNewRule((prev) => ({ ...prev, profileId: event.target.value }))}
                  required
                >
                  <option value="">Select profile</option>
                  {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.name}</option>
                  ))}
                </select>
                <select
                  value={newRule.scopeType}
                  onChange={(event) => setNewRule((prev) => ({ ...prev, scopeType: event.target.value as PricingScopeType }))}
                >
                  {RULE_SCOPES.map((scope) => (
                    <option key={scope} value={scope}>{scope}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newRule.scopeValue}
                  onChange={(event) => setNewRule((prev) => ({ ...prev, scopeValue: event.target.value }))}
                  placeholder="Scope value"
                />
                <input
                  type="number"
                  value={newRule.gpRateBasisPoints}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setNewRule((prev) => ({
                      ...prev,
                      gpRateBasisPoints: Number.isFinite(parsed) ? parsed : 0,
                    }));
                  }}
                  placeholder="Rate bps"
                />
                <input
                  type="number"
                  value={newRule.priority}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setNewRule((prev) => ({
                      ...prev,
                      priority: Number.isFinite(parsed) ? parsed : 0,
                    }));
                  }}
                  placeholder="Priority"
                />
                <input
                  type="date"
                  value={newRule.validFrom}
                  onChange={(event) => setNewRule((prev) => ({ ...prev, validFrom: event.target.value }))}
                />
                <input
                  type="date"
                  value={newRule.validTo}
                  onChange={(event) => setNewRule((prev) => ({ ...prev, validTo: event.target.value }))}
                />
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={newRule.isActive}
                    onChange={(event) => setNewRule((prev) => ({ ...prev, isActive: event.target.checked }))}
                  />
                  Active
                </label>
                <button type="submit" className="btn btn-primary">Create Rule</button>
              </form>

              <div className="admin-list">
                {rules.map((rule) => (
                  <form
                    key={rule.id}
                    className="admin-list-item"
                    onSubmit={(event) => handleUpdateRule(event, rule.id)}
                  >
                    <div>
                      <strong>{rule.scopeType}</strong>
                      <span className="muted">{rule.scopeValue ?? 'All'}</span>
                    </div>
                    <div className="admin-fields">
                      <select name="scopeType" defaultValue={rule.scopeType}>
                        {RULE_SCOPES.map((scope) => (
                          <option key={scope} value={scope}>{scope}</option>
                        ))}
                      </select>
                      <input name="scopeValue" defaultValue={rule.scopeValue ?? ''} />
                      <input name="gpRateBasisPoints" type="number" defaultValue={rule.gpRateBasisPoints ?? ''} />
                      <input name="priority" type="number" defaultValue={rule.priority} />
                      <input name="validFrom" type="date" defaultValue={rule.validFrom?.slice(0, 10) ?? ''} />
                      <input name="validTo" type="date" defaultValue={rule.validTo?.slice(0, 10) ?? ''} />
                      <select name="isActive" defaultValue={String(rule.isActive)}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="admin-actions">
                      <button type="submit" className="btn btn-outline">Save</button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Disable
                      </button>
                    </div>
                  </form>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container">
          <div className="admin-grid">
            <div className="admin-panel">
              <h2>Contracts</h2>
              <form className="admin-form" onSubmit={handleCreateContract}>
                <input
                  type="text"
                  value={newContract.customerExternalId}
                  onChange={(event) => setNewContract((prev) => ({ ...prev, customerExternalId: event.target.value }))}
                  placeholder="HaloPSA/MSP AI customer ID"
                  required
                />
                <select
                  value={newContract.profileId}
                  onChange={(event) => setNewContract((prev) => ({ ...prev, profileId: event.target.value }))}
                  required
                >
                  <option value="">Select profile</option>
                  {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={newContract.validFrom}
                  onChange={(event) => setNewContract((prev) => ({ ...prev, validFrom: event.target.value }))}
                />
                <input
                  type="date"
                  value={newContract.validTo}
                  onChange={(event) => setNewContract((prev) => ({ ...prev, validTo: event.target.value }))}
                />
                <textarea
                  value={newContract.notes}
                  onChange={(event) => setNewContract((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="Notes"
                />
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={newContract.isActive}
                    onChange={(event) => setNewContract((prev) => ({ ...prev, isActive: event.target.checked }))}
                  />
                  Active
                </label>
                <button type="submit" className="btn btn-primary">Create Contract</button>
              </form>

              <div className="admin-list">
                {contracts.map((contract) => (
                  <form
                    key={contract.id}
                    className="admin-list-item"
                    onSubmit={(event) => handleUpdateContract(event, contract.id)}
                  >
                    <div>
                      <strong>{contract.customerExternalId}</strong>
                      <span className="muted">Profile: {contract.profileId}</span>
                    </div>
                    <div className="admin-fields">
                      <input name="customerExternalId" defaultValue={contract.customerExternalId} />
                      <select name="profileId" defaultValue={contract.profileId}>
                        {profiles.map((profile) => (
                          <option key={profile.id} value={profile.id}>{profile.name}</option>
                        ))}
                      </select>
                      <input name="validFrom" type="date" defaultValue={contract.validFrom?.slice(0, 10) ?? ''} />
                      <input name="validTo" type="date" defaultValue={contract.validTo?.slice(0, 10) ?? ''} />
                      <select name="isActive" defaultValue={String(contract.isActive)}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <textarea name="notes" defaultValue={contract.notes ?? ''} />
                    <div className="admin-actions">
                      <button type="submit" className="btn btn-outline">Save</button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => handleDeleteContract(contract.id)}
                      >
                        Disable
                      </button>
                    </div>
                  </form>
                ))}
              </div>
            </div>

            <div className="admin-panel">
              <h2>Pricing Simulator</h2>
              <form className="admin-form" onSubmit={handleSimulate}>
                <input
                  type="number"
                  value={simulation.brandId}
                  onChange={(event) => setSimulation((prev) => ({ ...prev, brandId: event.target.value }))}
                  placeholder="Brand ID"
                  required
                />
                <input
                  type="text"
                  value={simulation.productId}
                  onChange={(event) => setSimulation((prev) => ({ ...prev, productId: event.target.value }))}
                  placeholder="Product ID"
                  required
                />
                <input
                  type="text"
                  value={simulation.customerExternalId}
                  onChange={(event) => setSimulation((prev) => ({ ...prev, customerExternalId: event.target.value }))}
                  placeholder="Customer ID (optional)"
                />
                <button type="submit" className="btn btn-primary">Simulate Pricing</button>
              </form>

              {simulationResult && (
                <div className="simulation-result">
                  <h3>Result</h3>
                  <p><strong>Cost:</strong> {simulationResult.costPriceCents} cents</p>
                  <p><strong>Sell:</strong> {simulationResult.sellPriceCents} cents</p>
                  <p><strong>Rate BPS:</strong> {simulationResult.gpRateBasisPoints}</p>
                  <p><strong>Rate Type:</strong> {simulationResult.rateType}</p>
                  <p><strong>Profile:</strong> {simulationResult.profileType ?? 'Unknown'}</p>
                  {simulationResult.appliedRuleId && (
                    <p><strong>Rule:</strong> {simulationResult.appliedRuleScope}</p>
                  )}
                </div>
              )}
            </div>

            <div className="admin-panel">
              <h2>Contract Sync</h2>
              <form className="admin-form" onSubmit={handleSyncContracts}>
                <select
                  value={syncForm.source}
                  onChange={(event) => setSyncForm((prev) => ({ ...prev, source: event.target.value }))}
                >
                  <option value="halo">HaloPSA</option>
                  <option value="msp-ai">MSP AI</option>
                </select>
                <input
                  type="number"
                  value={syncForm.clientHaloId}
                  onChange={(event) => setSyncForm((prev) => ({ ...prev, clientHaloId: event.target.value }))}
                  placeholder="Client Halo ID (optional)"
                />
                <input
                  type="number"
                  value={syncForm.contractHaloId}
                  onChange={(event) => setSyncForm((prev) => ({ ...prev, contractHaloId: event.target.value }))}
                  placeholder="Contract Halo ID (optional)"
                />
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={syncForm.dryRun}
                    onChange={(event) => setSyncForm((prev) => ({ ...prev, dryRun: event.target.checked }))}
                  />
                  Dry run
                </label>
                <button type="submit" className="btn btn-primary">
                  Run Sync
                </button>
              </form>

              {syncStatus === 'loading' && <p className="status-message">Syncing contracts...</p>}
              {syncError && <p className="status-message status-error">{syncError}</p>}
              {syncResult && (
                <div className="simulation-result">
                  <h3>Result</h3>
                  <p><strong>Source:</strong> {syncResult.source}</p>
                  <p><strong>Total:</strong> {syncResult.totalContracts}</p>
                  <p><strong>Processed:</strong> {syncResult.processed}</p>
                  <p><strong>Profiles:</strong> +{syncResult.createdProfiles} / ~{syncResult.updatedProfiles}</p>
                  <p><strong>Contracts:</strong> +{syncResult.createdContracts} / ~{syncResult.updatedContracts}</p>
                  <p><strong>Skipped:</strong> {syncResult.skipped}</p>
                  {syncResult.errors.length > 0 && (
                    <div>
                      <p><strong>Errors:</strong></p>
                      <ul>
                        {syncResult.errors.map((err, index) => (
                          <li key={`${err.contractId ?? 'contract'}-${index}`}>
                            {err.contractId ? `${err.contractId}: ` : ''}{err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingAdminPage;
